import React from "react";
import call from "./call";
import type { Output } from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { indexBy } from "diagnostics-lib/iterable";
import useActiveAnalysesStream from "./useActiveAnalysesStream";
import { comparatorBy } from "diagnostics-lib/comparator";
import { useFaultEvents } from "./useFaultEvents";
import useProjectSetupMode from "../components/SetupMode/useProjectSetupMode";

export type CommentType = Output<"GET", "/comments">["body"][number]["type"];

const comparator = comparatorBy("createdAt");

export const useComments = (projectId: string) => {
  const { isSetupModeEnabledOnProject } = useProjectSetupMode(projectId);
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const { events: faultEvents } = useFaultEvents(projectId);
  const faultEventsById = React.useMemo(() => indexBy(faultEvents, "id"), [faultEvents]);
  const activeAnalysesByid = React.useMemo(() => indexBy(activeAnalyses, "id"), [activeAnalyses]);
  const comments = useQuery("/comments", { query: { projectId } }).body;

  const getCachedComments = useGetQueryData("/comments", { query: { projectId } });
  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { comments, deletedCommentIds } = (
        await call("GET", "/comments/updates", {
          query: { projectId, since: new Date(since) },
        })
      ).body;
      return [comments, deletedCommentIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "comments", projectId },
    {
      readItems: React.useCallback(() => getCachedComments()?.body, [getCachedComments]),
      fetchUpdatedItems,
      mutateItems: useMutateBody("/comments", { query: { projectId } }),
    },
  );

  return React.useMemo(
    () =>
      comments
        .filter(c => isSetupModeEnabledOnProject || c.type === "RUN")
        .map(c => {
          const analysis = c.analysisId ? (activeAnalysesByid.get(c.analysisId) ?? null) : null;
          const faultEvent = c.faultEventId ? (faultEventsById.get(c.faultEventId) ?? null) : null;

          return {
            ...c,
            faultEvent,
            metadata: analysis
              ? {
                  date: analysis.date,
                  label: analysis.label,
                  group: analysis.group,
                }
              : null,
          };
        })
        .sort(comparator),
    [comments, activeAnalysesByid, faultEventsById, isSetupModeEnabledOnProject],
  );
};

export type Comment = ReturnType<typeof useComments>[number];
export type FaultEventComment = Comment & { faultEvent: NonNullable<Comment["faultEvent"]> };
